import {
  Button,
  Heading,
  Icon,
  Modal,
  SegmentGroup,
  Select,
  Spinner,
  Text,
} from "@ttc3k/trekker";
import { formatDistanceToNow } from "date-fns";
import { DatabaseSearch } from "iconoir-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Flex } from "styled-system/jsx";
import { SearchInput } from "shared/components/atoms/SearchInput/SearchInput";
import { OrgOverviewTable } from "./OrgOverviewTable";
import {
  FILTER_ALL,
  useOrgOverviewModal,
  type FilterOption,
} from "./useOrgOverviewModal";
import { LISTING_COLUMNS, OrgOverviewTab, PEOPLE_COLUMNS, EVENTS_COLUMNS } from "./orgOverviewColumns";

type OrgOverviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
};

export const OrgOverviewModal = ({
  isOpen,
  onClose,
  organizationId,
}: OrgOverviewModalProps) => {
  const { t } = useTranslation("core", { keyPrefix: "ORG_OVERVIEW" });

  const {
    activeTab,
    onTabChange,
    searchValue,
    onSearchValueChange,
    sortColumn,
    sortDirection,
    onSortChange,
    rows,
    isFilterActive,
    listingsTotal,
    listingsFilteredCount,
    peopleTotal,
    peopleFilteredCount,
    eventsTotal,
    eventsFilteredCount,
    loading,
    hasBuiltIndex,
    builtAt,
    isRebuilding,
    onRebuild,
    isListingsTab,
    isEventsTab,
    statusFilter,
    onStatusFilterChange,
    statusOptions,
    membershipTierFilter,
    onMembershipTierFilterChange,
    membershipTierOptions,
    membershipStatusFilter,
    onMembershipStatusFilterChange,
    membershipStatusOptions,
  } = useOrgOverviewModal({ organizationId, isOpen });

  const listingsCountLabel = isFilterActive
    ? t("TABS.COUNT_FILTERED", { filtered: listingsFilteredCount, total: listingsTotal })
    : t("TABS.COUNT_TOTAL", { count: listingsTotal });
  const peopleCountLabel = isFilterActive
    ? t("TABS.COUNT_FILTERED", { filtered: peopleFilteredCount, total: peopleTotal })
    : t("TABS.COUNT_TOTAL", { count: peopleTotal });
  const eventsCountLabel = isFilterActive
    ? t("TABS.COUNT_FILTERED", { filtered: eventsFilteredCount, total: eventsTotal })
    : t("TABS.COUNT_TOTAL", { count: eventsTotal });

  const columns =
    activeTab === OrgOverviewTab.Listings
      ? LISTING_COLUMNS
      : activeTab === OrgOverviewTab.People
        ? PEOPLE_COLUMNS
        : EVENTS_COLUMNS;

  const toCollection = (options: FilterOption[]) =>
    Select.createCollection({ items: options });

  const statusCollection = useMemo(
    () => toCollection(statusOptions),
    [statusOptions],
  );
  const membershipTierCollection = useMemo(
    () => toCollection(membershipTierOptions),
    [membershipTierOptions],
  );
  const membershipStatusCollection = useMemo(
    () => toCollection(membershipStatusOptions),
    [membershipStatusOptions],
  );

  return (
    <Modal
      open={isOpen}
      onOpenChange={({ open }: { open: boolean }) => !open && onClose()}
      placement="fullPane"
      portal
    >
      <Flex direction="column" h="80vh" maxH="900px" p="500" gap="400">
        <Flex
          align="center"
          justify="space-between"
          gap="300"
          flexWrap="wrap"
          pr="600"
        >
          <Flex align="center" gap="150">
            <Icon Element={DatabaseSearch} size="md" color="icon.mid" />
            <Heading visual="h4">{t("TITLE")}</Heading>
          </Flex>

          <Flex direction="column" align="flex-end" gap="50">
            <Button
              visual="primary"
              size="sm"
              isLoading={isRebuilding}
              onClick={onRebuild}
            >
              {t("REBUILD_BUTTON")}
            </Button>
            {builtAt && (
              <Text visual="smallRegular" color="text.light">
                {t("LAST_BUILT", {
                  time: formatDistanceToNow(new Date(builtAt), {
                    addSuffix: true,
                  }),
                })}
              </Text>
            )}
          </Flex>
        </Flex>

        <Flex align="center" gap="300" flexWrap="nowrap">
          <SearchInput
            placeholder={t("SEARCH_PLACEHOLDER")}
            value={searchValue}
            onChange={e => onSearchValueChange(e.target.value)}
            onClearSearch={() => onSearchValueChange("")}
            _containerProps={{ w: "40%", flexShrink: 0 }}
          />

          <SegmentGroup.Root
            segmentStyle="buttons"
            orientation="horizontal"
            value={activeTab}
            onValueChange={value => onTabChange(value.value as OrgOverviewTab)}
            h="auto"
            flex="1"
          >
            <SegmentGroup.Item value={OrgOverviewTab.Listings} whiteSpace="nowrap">
              {t("TABS.LISTINGS")} {listingsCountLabel}
            </SegmentGroup.Item>
            <SegmentGroup.Item value={OrgOverviewTab.People} whiteSpace="nowrap">
              {t("TABS.PEOPLE")} {peopleCountLabel}
            </SegmentGroup.Item>
            <SegmentGroup.Item value={OrgOverviewTab.Events} whiteSpace="nowrap">
              {t("TABS.EVENTS")} {eventsCountLabel}
            </SegmentGroup.Item>
          </SegmentGroup.Root>
        </Flex>

        <Flex align="center" gap="200" flexWrap="wrap">
          {isListingsTab && (
            <Select.Root
              collection={statusCollection}
              value={[statusFilter]}
              onValueChange={({ value }: { value: string[] }) =>
                onStatusFilterChange(value[0] ?? FILTER_ALL)
              }
              placeholder={t("FILTERS.STATUS_PLACEHOLDER")}
              size="md"
              width="180px"
            >
              {statusCollection.items.map(opt => (
                <Select.Item key={opt.value} item={opt} />
              ))}
            </Select.Root>
          )}
          {!isEventsTab && (
            <>
              <Select.Root
                collection={membershipTierCollection}
                value={[membershipTierFilter]}
                onValueChange={({ value }: { value: string[] }) =>
                  onMembershipTierFilterChange(value[0] ?? FILTER_ALL)
                }
                placeholder={t("FILTERS.MEMBERSHIP_TIER_PLACEHOLDER")}
                size="md"
                width="200px"
              >
                {membershipTierCollection.items.map(opt => (
                  <Select.Item key={opt.value} item={opt} />
                ))}
              </Select.Root>
              <Select.Root
                collection={membershipStatusCollection}
                value={[membershipStatusFilter]}
                onValueChange={({ value }: { value: string[] }) =>
                  onMembershipStatusFilterChange(value[0] ?? FILTER_ALL)
                }
                placeholder={t("FILTERS.MEMBERSHIP_STATUS_PLACEHOLDER")}
                size="md"
                width="200px"
              >
                {membershipStatusCollection.items.map(opt => (
                  <Select.Item key={opt.value} item={opt} />
                ))}
              </Select.Root>
            </>
          )}
        </Flex>

        {loading && !hasBuiltIndex ? (
          <Flex justify="center" align="center" flex="1">
            <Spinner width="40px" height="40px" />
          </Flex>
        ) : (
          <Flex
            direction="column"
            flex="1"
            minH="0"
            borderWidth="1px"
            borderColor="border.light"
            borderRadius="200"
            overflow="hidden"
          >
            <OrgOverviewTable
              columns={columns}
              rows={rows}
              entityType={
                activeTab === OrgOverviewTab.Listings
                  ? "LISTING"
                  : activeTab === OrgOverviewTab.People
                    ? "PERSON"
                    : "EVENT"
              }
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSortChange={onSortChange}
              organizationId={organizationId}
              loading={loading}
              hasBuiltIndex={hasBuiltIndex}
              onClose={onClose}
            />
          </Flex>
        )}
      </Flex>
    </Modal>
  );
};
